/**
 * AudioManager - Manages music playback and ambient sound transitions
 * 
 * Features:
 * - Plays random tracks from the music library
 * - Plays ambient sounds between tracks (30-60 seconds)
 * - Shuffles track list and repeats after all tracks have been played
 */

export class AudioManager {
    private scene: Phaser.Scene;
    private tracks: string[] = [
        'echoes1',
        'echoes2',
        'echoes3',
        'echoes4',
        'echoes5',
        'echoes6'
    ];
    private availableTracks: string[] = [];
    private currentTrack: Phaser.Sound.BaseSound | null = null;
    private ambientSound: Phaser.Sound.BaseSound | null = null;
    private isPlayingAmbient: boolean = false;
    private ambientTimer: Phaser.Time.TimerEvent | null = null;
    private loadingTrack: string | null = null;
    private isDestroyed: boolean = false;

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
        this.shuffleTracks();
    }

    /**
     * Preload all music and ambient sound files
     * Note: This is now a no-op. Music is loaded asynchronously on-demand.
     */
    static preload(scene: Phaser.Scene): void {
        // Music tracks are loaded asynchronously on-demand to prevent blocking scene load
        // Only load the ambient sound during preload as it's small
        scene.load.audio('ambient', '/sfx/ambient.ogg');
    }

    /**
     * Shuffle the available tracks list
     */
    private shuffleTracks(): void {
        this.availableTracks = [...this.tracks];
        // Fisher-Yates shuffle
        for (let i = this.availableTracks.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.availableTracks[i], this.availableTracks[j]] = [this.availableTracks[j], this.availableTracks[i]];
        }
    }

    /**
     * Start playing music - call this when entering main scene
     */
    start(): void {
        if (localStorage.getItem('musicEnabled') === 'false') {
            return;
        }
        this.playNextTrack();
    }

    /**
     * Play the next available track
     */
    private playNextTrack(): void {
        if (localStorage.getItem('musicEnabled') === 'false') {
            return;
        }
        // If no tracks available, reshuffle and start over
        if (this.availableTracks.length === 0) {
            this.shuffleTracks();
        }

        // Get next track
        const trackKey = this.availableTracks.pop();
        if (!trackKey) {
            console.warn('AudioManager: No tracks available to play');
            return;
        }

        // Stop current track if playing
        if (this.currentTrack) {
            this.currentTrack.stop();
            this.currentTrack = null;
        }

        // Load and play the track asynchronously
        if (!this.scene.cache.audio.exists(trackKey)) {
            // Track not loaded yet, load it asynchronously
            this.loadingTrack = trackKey;
            const trackPath = `/music/${trackKey}.mp3`;
            this.scene.load.audio(trackKey, trackPath);

            this.scene.load.once('complete', () => {
                // Check if manager was destroyed during loading
                if (this.isDestroyed || this.loadingTrack !== trackKey) {
                    return;
                }

                this.loadingTrack = null;

                if (this.scene.cache.audio.exists(trackKey)) {
                    this.currentTrack = this.scene.sound.add(trackKey, { volume: 0.4 });

                    // Listen for track completion
                    this.currentTrack.once('complete', () => {
                        this.playAmbient();
                    });

                    this.currentTrack.play();
                }
            });

            this.scene.load.start();
        } else {
            // Track already loaded, just play it
            this.currentTrack = this.scene.sound.add(trackKey, { volume: 0.4 });

            // Listen for track completion
            this.currentTrack.once('complete', () => {
                this.playAmbient();
            });

            this.currentTrack.play();
        }
    }

    /**
     * Play ambient sound for a random duration between 30-60 seconds
     */
    private playAmbient(): void {
        // Random duration between 30000ms (30s) and 60000ms (60s)
        const duration = Phaser.Math.Between(30000, 60000);

        if (localStorage.getItem('sfxEnabled') === 'false') {
            // SFX disabled — skip ambient, just wait and play next track
            this.ambientTimer = this.scene.time.delayedCall(duration, () => {
                this.stopAmbient();
                this.playNextTrack();
            });
            return;
        }

        this.isPlayingAmbient = true;

        // Play ambient sound on loop
        this.ambientSound = this.scene.sound.add('ambient', {
            volume: 0.3,
            loop: true
        });
        this.ambientSound.play();

        // Set timer to stop ambient and play next track
        this.ambientTimer = this.scene.time.delayedCall(duration, () => {
            this.stopAmbient();
            this.playNextTrack();
        });
    }

    /**
     * Stop ambient sound playback
     */
    stopAmbient(): void {
        if (this.ambientSound) {
            this.ambientSound.stop();
            this.ambientSound = null;
        }

        if (this.ambientTimer) {
            this.ambientTimer.destroy();
            this.ambientTimer = null;
        }

        this.isPlayingAmbient = false;
    }

    /**
     * Stop all audio playback
     */
    stop(): void {
        if (this.currentTrack) {
            this.currentTrack.stop();
            this.currentTrack = null;
        }

        this.stopAmbient();
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.isDestroyed = true;
        this.loadingTrack = null;
        this.stop();
    }
}
